import { z } from "zod";
import { zodI18nMap } from "zod-i18n-map";

export const date = z.union([
  z.string().transform((elem) => new Date(elem)),
  z.date(),
]);

export type Serialized<T> = {
  [K in keyof T]: T[K] extends Date
    ? string
    : T[K] extends object
      ? Serialized<T[K]>
      : T[K];
};

export function deserializeSchema(
  schema: z.ZodDate,
): z.ZodEffects<z.ZodString, Date, string>;
export function deserializeSchema<T extends z.ZodTypeAny>(schema: T): T;
export function deserializeSchema(schema: z.ZodTypeAny): z.ZodTypeAny {
  // Handle ZodDate - convert to string with transform back to Date
  if (schema instanceof z.ZodDate) {
    const result = z.string().transform((val) => new Date(val));
    return result;
  }

  // Handle ZodObject - recursively process all properties
  if (schema instanceof z.ZodObject) {
    const shape = schema.shape;
    const newShape: Record<string, z.ZodTypeAny> = {};

    for (const [key, value] of Object.entries(shape)) {
      newShape[key] = deserializeSchema(value as any);
    }

    return z.object(newShape);
  }

  // Handle ZodArray - deserialize the element type
  if (schema instanceof z.ZodArray) {
    return z.array(deserializeSchema(schema.element));
  }

  // Handle ZodOptional - deserialize the inner type
  if (schema instanceof z.ZodOptional) {
    return deserializeSchema(schema.unwrap()).optional();
  }

  // Handle ZodNullable - deserialize the inner type
  if (schema instanceof z.ZodNullable) {
    return deserializeSchema(schema.unwrap()).nullable();
  }

  // Handle ZodUnion - deserialize all options
  if (schema instanceof z.ZodUnion) {
    const options = schema.options.map((option: z.ZodTypeAny) =>
      deserializeSchema(option),
    );
    return z.union(options as any);
  }

  // For all other types, return as-is
  return schema;
}

export function loadZodTranslations() {
  z.setErrorMap(zodI18nMap);
}

export type Weekday = z.infer<typeof weekdaySchema>;
export const weekdaySchema = z.enum([
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
  "sun",
]);
